import {COVER_IMAGE_SIZE_CONFIG} from '#/lib/api/articles'
import {compressIfNeeded} from '#/lib/media/manip'
import {type PickerImage} from '#/lib/media/picker.shared'

/**
 * The real byte length of a picked image.
 *
 * `PickerImage.size` cannot be trusted for this: it comes from
 * `getDataUriSize(uri)`, which infers a **data:** URI's size from its string
 * length. On web the picker returns a `blob:` URL of a few dozen characters,
 * so every image reports the same tiny number regardless of its actual size.
 *
 * Fetching the URI back is the one measurement that works on both platforms -
 * `blob:`, `data:` and `file:` URLs all resolve, and the bytes are already
 * local, so nothing goes over the network.
 *
 * Returns `undefined` when the size genuinely cannot be determined. Body-image
 * callers treat that as "allow", deliberately: the PDS enforces its own
 * ceiling, and refusing an image because we failed to measure it would block
 * a legitimate upload on our own shortcoming.
 */
export async function measureImageBytes(image: {
  path: string
  size?: number
}): Promise<number | undefined> {
  try {
    const response = await fetch(image.path)
    const blob = await response.blob()
    if (blob.size > 0) return blob.size
  } catch {
    // Fall through to the picker's own figure below.
  }
  return image.size && image.size > 0 ? image.size : undefined
}

/**
 * Brings a picked cover image within `COVER_IMAGE_SIZE_CONFIG`, shrinking it
 * the way upstream shrinks avatars when it is over.
 *
 * `compressIfNeeded` decides from `image.size`, which is wrong on web (see
 * `measureImageBytes`), so it is given the measured size instead. An image
 * whose size can't be measured is shrunk anyway: a cover over the limit would
 * only fail later, at publish. Throws when the image can't be made to fit.
 */
export async function prepareCoverImage(
  image: PickerImage,
): Promise<PickerImage> {
  const bytes = await measureImageBytes(image)
  return await compressIfNeeded(
    {...image, size: bytes ?? Number.POSITIVE_INFINITY},
    COVER_IMAGE_SIZE_CONFIG,
  )
}
